import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Customer } from '../../types/invoice';

export interface CustomerFormData {
  name: string;
  ico: string;
  dic: string;
  address: string;
  /** Combined format: "123456/0100" or "123456-789012/0100" */
  bankAccount: string;
  email: string;
  phone: string;
  notes: string;
}

const emptyForm: CustomerFormData = {
  name: '',
  ico: '',
  dic: '',
  address: '',
  bankAccount: '',
  email: '',
  phone: '',
  notes: '',
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CustomerFormData) => Promise<void>;
  initialData?: Partial<CustomerFormData>;
  title?: string;
  submitLabel?: string;
  errorMessage?: string | null;
  duplicateCustomer?: Customer | null;
}

const CustomerForm: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  title = 'Dodavatel',
  submitLabel = 'Uložit',
  errorMessage,
  duplicateCustomer,
}) => {
  const [form, setForm] = useState<CustomerFormData>({ ...emptyForm, ...initialData });
  const [saving, setSaving] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, ...initialData });
      setAresError(null);
    }
  }, [open, initialData]);

  const handleChange = (field: keyof CustomerFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAresLookup = async () => {
    const ico = form.ico.trim();
    if (!ico) {
      setAresError('Zadejte IČO pro vyhledání v ARES');
      return;
    }
    setAresLoading(true);
    setAresError(null);
    try {
      const resp = await fetch(
        `https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${ico}`
      );
      if (!resp.ok) {
        setAresError('Subjekt v ARES nebyl nalezen');
        return;
      }
      const data = await resp.json();
      const address = [
        data.sidlo?.nazevUlice,
        data.sidlo?.cisloDomovni
          ? `${data.sidlo.cisloDomovni}${data.sidlo.cisloOrientacni ? '/' + data.sidlo.cisloOrientacni : ''}`
          : null,
        data.sidlo?.nazevObce,
        data.sidlo?.psc,
      ]
        .filter(Boolean)
        .join(', ');

      setForm((prev) => ({
        ...prev,
        name: data.obchodniJmeno || prev.name,
        dic: data.dic || prev.dic,
        address: address || prev.address,
      }));
    } catch {
      setAresError('Nepodařilo se připojit k ARES');
    } finally {
      setAresLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
            {duplicateCustomer && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Existující dodavatel: {duplicateCustomer.name}
                </Typography>
                {duplicateCustomer.ico && (
                  <Typography variant="body2">IČO: {duplicateCustomer.ico}</Typography>
                )}
                {duplicateCustomer.dic && (
                  <Typography variant="body2">DIČ: {duplicateCustomer.dic}</Typography>
                )}
              </Box>
            )}
          </Alert>
        )}
        {aresError && (
          <Alert severity="warning" onClose={() => setAresError(null)} sx={{ mb: 2 }}>
            {aresError}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* IČO + ARES lookup */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="IČO"
              value={form.ico}
              onChange={handleChange('ico')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={aresLoading ? <CircularProgress size={16} /> : <SearchIcon />}
              onClick={handleAresLookup}
              disabled={aresLoading || !form.ico.trim()}
              fullWidth
              size="medium"
            >
              {aresLoading ? 'Vyhledávám...' : 'Vyhledat v ARES'}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="DIČ"
              value={form.dic}
              onChange={handleChange('dic')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* spacer */}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Název *"
              value={form.name}
              onChange={handleChange('name')}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Adresa"
              value={form.address}
              onChange={handleChange('address')}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Číslo účtu"
              value={form.bankAccount}
              onChange={handleChange('bankAccount')}
              fullWidth
              placeholder="123456-789012/0100"
              helperText="Formát: číslo účtu/kód banky, např. 2001241518/2600"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="E-mail"
              value={form.email}
              onChange={handleChange('email')}
              fullWidth
              type="email"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Telefon"
              value={form.phone}
              onChange={handleChange('phone')}
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Poznámky"
              value={form.notes}
              onChange={handleChange('notes')}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Zrušit
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !form.name.trim()}
        >
          {saving ? <CircularProgress size={20} /> : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerForm;
